import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getClosingApprovals,
  rejectClosingRequest
} from '../../services/leadService';
import { LeadClosingRequest } from '../../types/leads';
import ClosingApprovalModal from './components/ClosingApprovalModal';

const { Title, Text } = Typography;

const ClosingApprovals = () => {
  const [requests, setRequests] = useState<LeadClosingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [activeRequest, setActiveRequest] = useState<LeadClosingRequest | null>(null);
  const [rejectForm] = Form.useForm();
  const [submittingReject, setSubmittingReject] = useState(false);

  // State for Reusable Approval Modal
  const [approveModalVisible, setApproveModalVisible] = useState(false);

  const navigate = useNavigate();
  const { message } = App.useApp();

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClosingApprovals();
      if (response.data?.status === 'success') {
        setRequests(response.data.data || []);
      } else {
        message.error('Gagal mengambil daftar antrean persetujuan.');
      }
    } catch (error: any) {
      console.error('Fetch approvals failed:', error);
      message.error(error.response?.data?.message || 'Gagal memuat antrean persetujuan.');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleOpenApproveModal = (record: LeadClosingRequest) => {
    setActiveRequest(record);
    setApproveModalVisible(true);
  };

  const handleOpenRejectModal = (record: LeadClosingRequest) => {
    setActiveRequest(record);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    if (!activeRequest) return;
    try {
      const values = await rejectForm.validateFields();
      setSubmittingReject(true);
      const response = await rejectClosingRequest(activeRequest.id, {
        rejection_reason: values.rejection_reason,
      });

      if (response.data?.status === 'success') {
        message.success('Pengajuan closing berhasil ditolak.');
        setRejectModalVisible(false);
        setActiveRequest(null);
        rejectForm.resetFields();
        fetchApprovals();
      } else {
        message.error(response.data?.message || 'Gagal menolak pengajuan.');
      }
    } catch (error: any) {
      console.error('Reject closing failed:', error);
      message.error(error.response?.data?.message || 'Gagal menolak pengajuan.');
    } finally {
      setSubmittingReject(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const columns = [
    {
      title: 'Lead Name',
      dataIndex: 'lead_name',
      key: 'lead_name',
      render: (text: string, record: LeadClosingRequest) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || record.lead?.full_name || 'N/A'}</Text>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => navigate(`/leads/approvals/${record.lead_id}`)}
          >
            Lihat Detail Lead
          </Button>
        </Space>
      ),
    },
    {
      title: 'Kawasan',
      dataIndex: 'property_area_name',
      key: 'property_area_name',
      render: (area: string) => <Tag color="cyan">{area || 'N/A'}</Tag>,
    },
    {
      title: 'Sales Agent',
      dataIndex: 'submitted_by_name',
      key: 'submitted_by_name',
      render: (sales: string) => <Text>{sales || 'N/A'}</Text>,
    },
    {
      title: 'Pembeli',
      dataIndex: 'buyer_name',
      key: 'buyer_name',
      render: (buyer: string, record: LeadClosingRequest) => (
        <Space direction="vertical" size={0}>
          <Text strong>{buyer}</Text>
          {record.buyer_npwp && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              NPWP: {record.buyer_npwp}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Detail Properti Unit',
      dataIndex: 'purchased_property_detail',
      key: 'purchased_property_detail',
      render: (unit: string) => <Text>{unit}</Text>,
    },
    {
      title: 'Nilai Deal (Omzet)',
      dataIndex: 'omzet',
      key: 'omzet',
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong style={{ color: '#7BAA18' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Tanggal Pengajuan',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD MMM YYYY, HH:mm'),
    },
    {
      title: 'Aksi',
      key: 'action',
      align: 'center' as const,
      render: (_: any, record: LeadClosingRequest) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18' }}
            onClick={() => handleOpenApproveModal(record)}
          >
            Approve
          </Button>

          <Button
            type="primary"
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleOpenRejectModal(record)}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        Antrean Persetujuan Closing Lead
      </Title>

      <Card>
        <Table
          dataSource={requests}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Tidak ada pengajuan closing pending saat ini.' }}
        />
      </Card>

      {/* Reusable Closing Approval Modal with SP Document Upload & Live Mini Preview */}
      <ClosingApprovalModal
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          setActiveRequest(null);
        }}
        onSuccess={() => {
          setApproveModalVisible(false);
          setActiveRequest(null);
          fetchApprovals();
        }}
        request={activeRequest}
      />

      {/* Rejection Reason Modal */}
      <Modal
        title="Tolak Pengajuan Closing OK"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setActiveRequest(null);
          rejectForm.resetFields();
        }}
        onOk={handleRejectSubmit}
        confirmLoading={submittingReject}
        okText="Tolak Pengajuan"
        cancelText="Batal"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <p>
            Tolak pengajuan closing untuk pembeli <strong>{activeRequest?.buyer_name}</strong> senilai{' '}
            <strong>{formatCurrency(activeRequest?.omzet || 0)}</strong>.
          </p>
          <Form.Item
            name="rejection_reason"
            label="Alasan Penolakan"
            rules={[{ required: true, message: 'Alasan penolakan wajib diisi!' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Masukkan alasan lengkap penolakan pengajuan closing ini agar sales agent dapat memperbaiki berkas/informasi..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClosingApprovals;
